import { DrawCabinet } from "$src/CGS/domain/dto/article/Cabinet/DrawCabinet";
import { DrawConstant } from "$src/CGS/domain/dto/constant/DrawConstant";
import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { MathUtils, Vector3 } from "three";
import { FrontHandleMetadataBuilder } from "./FrontHandleMetadataBuilder";
import { FrontElementMetadata, FrontGroupMetadata, FrontMetadata } from "../FrontMetadata";
import { FrontFaceMetadata } from "../FrontFaceMetadata";
import { ECabinetGeneratorErrorCode } from "../../Errors/ECabinetGeneratorErrorCode";
import { EOrientation, OrientationExtension } from "$src/CGS/domain/dto/core/EOrientation";
import { LengthValueMillimeter } from "$src/CGS/domain/dto/core/Length/LengthValueMillimeter";
import { DrawCabinetFrontElementGroup } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElementGroup";
import { EFrontElement } from "$src/CGS/domain/dto/core/EFrontElement";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";
import { CabinetGeneratorError } from "../../Errors/CabinetGeneratorError";
import { CabinetGeneratorErrorRegistry } from "../../Errors/CabinetGeneratorErrorRegistry";
import { FrontElementGroupComputation } from "../Compute/FrontElementGroupComputation";
import { FrontElementComputation } from "../Compute/FrontElementComputation";
import { InsideContainerBuilder } from "./InsideContainerBuilder";
import { DrawCabinetFrontElement } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElement";

export class FrontElementMetadataBuilder {
    private _constants: DrawConstant;
    private _cabinet: DrawCabinet;
    private _errorRegistry: CabinetGeneratorErrorRegistry;
    private _handleMetadataBuilder: FrontHandleMetadataBuilder;
    private _insideContainerBuilder: InsideContainerBuilder;

    constructor(cabinet: DrawCabinet, errorRegistry: CabinetGeneratorErrorRegistry) {
        this._cabinet = cabinet;
        this._constants = cabinet.Constants;
        this._errorRegistry = errorRegistry;
        this._handleMetadataBuilder = new FrontHandleMetadataBuilder(this._cabinet, this._errorRegistry);
        this._insideContainerBuilder = new InsideContainerBuilder(cabinet);
    }

    /**
     * Build FrontElementMetadata flat list based on the element.
     * Will compute the dimensions and positions of each element
     */
    public BuildFrontElements(frontFaces: FrontFaceMetadata[], element: FrontElementComputation, 
        availWidth: LengthValue, availHeight: LengthValue, 
        parentSuplusHeight: LengthValue,
        anchor: Vector3, parentGroup: FrontElementGroupComputation | null = null, parentGroupMetadata: FrontGroupMetadata | null = null): FrontMetadata[] {
        
        const frontElements: FrontMetadata[] = [];

        if (availHeight.millimeter < element.minHeight) {
            this._errorRegistry.raiseError(new CabinetGeneratorError(
                ECabinetGeneratorErrorCode.FrontElementHeightOverflowing,
                LengthMaths.getLengthValue(availHeight).millimeter,
                element.minHeight,
                element.context.FrontElement
            ));
        }
        if (availWidth.millimeter < element.minWidth) {
            this._errorRegistry.raiseError(new CabinetGeneratorError(
                ECabinetGeneratorErrorCode.FrontElementWidthOverflowing,
                LengthMaths.getLengthValue(availWidth).millimeter,
                element.minWidth,
                element.context.FrontElement
            ));
        }

        if (element instanceof FrontElementGroupComputation) {
            frontElements.push(...this.BuildGroup(frontFaces, element, availWidth, availHeight, parentSuplusHeight, anchor, parentGroup, parentGroupMetadata));
        } else {
            const frontElement = this.BuildFront(frontFaces, availWidth, availHeight, parentSuplusHeight, anchor, element, element.context, parentGroup, parentGroupMetadata);
            if (frontElement.Height.value !== 0) {
                frontElements.push(frontElement);
            }
        }

        return frontElements;
    }

    /**
     * Build group children and compute their dimensions
     */
    private BuildGroup(frontFaces: FrontFaceMetadata[], group: FrontElementGroupComputation,
        availWidth: LengthValue, availHeight: LengthValue,
        parentSurplusHeight: LengthValue,
        parentAnchor: Vector3, 
        parentGroup: FrontElementGroupComputation | null, parentGroupMetadata: FrontGroupMetadata | null): FrontMetadata[] {
        
        let groupAnchor = parentAnchor;
        const frontElements: FrontMetadata[] = [];

        const availLength = group.orientation === EOrientation.Horizontal ? availWidth.millimeter : availHeight.millimeter;
        const availOppositeLength = group.orientation === EOrientation.Horizontal ? availHeight.millimeter : availWidth.millimeter;

        // Available size in group
        const availableSurplusLength = availLength - group.getMin(group.orientation);

        // Total delta between max and min, in children with finite max value
        const totalRangeDelta = group.elements
            .filter(e => e.getMax(group.orientation) !== null)
            .reduce((sum, e) => sum + ((e.getMax(group.orientation) ?? 0) - e.getMin(group.orientation)), 0);

        // Number of children with infinite max
        const infiniteLengthCount = group.elements.filter(e => e.getMax(group.orientation) === null).length;

        // Build the group metadata
        const groupElement: FrontGroupMetadata = new FrontGroupMetadata();
        groupElement.Width = availWidth;
        groupElement.Height = availHeight;
        groupElement.Anchor = groupAnchor.clone();
        groupElement.ContextGroup = group.context as DrawCabinetFrontElementGroup;
        groupElement.ParentGroup = parentGroupMetadata;
        groupElement.Elements = frontElements;
        groupElement.FrontFaces = frontFaces;
        
        if (group.inside !== null) {
            groupElement.Inside = this._insideContainerBuilder.buildSingleInsideContainer(groupElement, group.inside, new LengthValueMillimeter(0), !group.hasElementBelow, !group.hasElementAbove);
        }

        for (const computation of group.elements) {
            // Compute the additional length to give to the current child
            let additionalLength: number;
            if (computation.getMax(group.orientation) === null) {
                // Infinite: split remaining amongst infinite children
                additionalLength = (availableSurplusLength - totalRangeDelta) / infiniteLengthCount;
            } else if (totalRangeDelta > 0) {
                // Finite: allocate a proportion of the remaining space
                if (totalRangeDelta > availableSurplusLength) {
                    additionalLength = availableSurplusLength * ((computation.getMax(group.orientation) ?? 0) - computation.getMin(group.orientation)) / totalRangeDelta;
                } else {
                    additionalLength = (computation.getMax(group.orientation) ?? 0) - computation.getMin(group.orientation);
                }
            } else {
                // No available space
                additionalLength = 0;
            }

            // Add the additional length to the minimum length
            const elemLength = computation.getMin(group.orientation) + additionalLength;
            let oppositeLength: number = MathUtils.clamp(
                availOppositeLength,
                computation.getMin( OrientationExtension.opposite(group.orientation)),
                computation.getMax( OrientationExtension.opposite(group.orientation)) ?? Number.MAX_SAFE_INTEGER
            );
            const availableSurplusHeight = group.orientation === EOrientation.Vertical ? availableSurplusLength : 0;
            const surplusHeightMm = Math.min(parentSurplusHeight.millimeter + availableSurplusHeight - additionalLength, (group.maxHeight ?? Number.MAX_VALUE) - availHeight.millimeter);
            const surplusHeight = new LengthValueMillimeter(surplusHeightMm);

            frontElements.push(...this.BuildChildren(frontFaces, group, computation, new LengthValueMillimeter(elemLength), new LengthValueMillimeter(oppositeLength),
                surplusHeight, groupAnchor, groupElement));
        }

        // Translate the parent group anchor to the end of the current group
        if (parentGroup !== null) {
            if (parentGroup.orientation === group.orientation) {
                parentAnchor = groupAnchor;
            } else {
                const maxElementWidth = Math.max(...frontElements.filter(e => !(e instanceof FrontGroupMetadata)).map(e => LengthMaths.getLengthValue(e.Width).meter));
                const maxElementHeight = Math.max(...frontElements.filter(e => !(e instanceof FrontGroupMetadata)).map(e => LengthMaths.getLengthValue(e.Height).meter));
                if (parentGroup.orientation === EOrientation.Horizontal) {
                    parentAnchor = parentAnchor.add(new Vector3(maxElementWidth + parentGroup.getGapBetweenElement(this._constants).meter, 0, 0));
                } else {
                    parentAnchor = parentAnchor.add(new Vector3(0, maxElementHeight + parentGroup.getGapBetweenElement(this._constants).meter, 0));
                }
            }
        }

        return [...frontElements, groupElement];
    }

    private BuildChildren(frontFaces: FrontFaceMetadata[], group: FrontElementGroupComputation, frontElementComputation: FrontElementComputation,
        length: LengthValue, oppositeLength: LengthValue,
        parentSurplusLength: LengthValue,
        groupAnchor: Vector3, parentGroup: FrontGroupMetadata): FrontMetadata[] {
        
        if (group.orientation === EOrientation.Horizontal) {
            return this.BuildFrontElements(frontFaces, frontElementComputation, length, oppositeLength, parentSurplusLength, groupAnchor, group, parentGroup);
        } else {
            return this.BuildFrontElements(frontFaces, frontElementComputation, oppositeLength, length, parentSurplusLength, groupAnchor, group, parentGroup);
        }
    }

    /**
     * Build a single front & translate the anchor for the next element
     */
    private BuildFront(frontFaces: FrontFaceMetadata[], width: LengthValue, height: LengthValue,
        parentSurplusHeight: LengthValue,
        anchor: Vector3, element: FrontElementComputation, context: DrawCabinetFrontElement, group: FrontElementGroupComputation | null, parentGroupMetadata: FrontGroupMetadata | null): FrontElementMetadata {
        
        let currentAnchor = anchor;

        // The width without the possible adaptation made for the groove to be larger (Will be used to compute the position of the next element)
        const frontElementWidth = new LengthValueMillimeter(MathUtils.clamp(width.millimeter, element.minWidth, element.maxWidth ?? Number.MAX_VALUE));

        // If the element is an horizontal groove, than remove the side box gap
        if (element.context.FrontElement === EFrontElement.G) {
            currentAnchor.sub(new Vector3(this._constants.SideBoxGap.meter, 0, 0));
            LengthMaths.add(width, LengthMaths.multiply(this._constants.SideBoxGap, 2));
        }

        // The actual width used to display the item
        const actualWidth = new LengthValueMillimeter(MathUtils.clamp(width.millimeter, element.minWidth, element.maxWidth ?? Number.MAX_VALUE));
        const actualHeight = new LengthValueMillimeter(MathUtils.clamp(height.millimeter, element.minHeight, element.maxHeight ?? Number.MAX_VALUE));

        // Build the metadata element
        const metadataElement: FrontElementMetadata = new FrontElementMetadata()
        metadataElement.Width = actualWidth;
        metadataElement.Height = actualHeight;
        metadataElement.Anchor = currentAnchor.clone();
        metadataElement.Context = element.context;
        metadataElement.ParentGroup = parentGroupMetadata;
        metadataElement.Handles = this._handleMetadataBuilder.buildFrontHandles(actualWidth, actualHeight, currentAnchor, element.context, group?.context as DrawCabinetFrontElementGroup);
        metadataElement.Type = element.context.FrontElement;
        metadataElement.FrontFaces = frontFaces;
        
        const surplusHeightMm = Math.min(parentSurplusHeight.millimeter, (element.maxHeight ?? Number.MAX_VALUE) - actualHeight.millimeter);
        const surplusHeight = new LengthValueMillimeter(surplusHeightMm);

        if (element.inside !== null) {
            metadataElement.Inside = this._insideContainerBuilder.buildSingleInsideContainer(metadataElement, element.inside, surplusHeight, !element.hasElementBelow, !element.hasElementAbove);
        }

        if (group !== null) {
            // Translate anchor to the next element
            if (group.orientation === EOrientation.Horizontal) {
                anchor.add(new Vector3( LengthMaths.add(frontElementWidth, group.getGapBetweenElement(this._constants)).meter, 0, 0));
            } else {
                const gapWithAboveElement = element.gapWithAboveElement(EOrientation.Vertical);
                const elementContextFrontElement = element?.context?.FrontElement === EFrontElement.G ? new LengthValueMillimeter(0) : gapWithAboveElement;
                anchor.add(new Vector3(0, LengthMaths.add(actualHeight, elementContextFrontElement).meter, 0));
            }
        }

        return metadataElement;
    }
}
